import React, {useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {Alert, Collapse, Input, Modal, Table, Tooltip} from "antd";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faCheckCircle, faCircleInfo, faExclamationTriangle, faSearch} from "@fortawesome/free-solid-svg-icons";
import {observer} from "mobx-react";
import {columnsFactory, HintDetails, hintExplanation} from "./cart-create-hints-table.columns";
import {CartHintRow, createHintRow} from "./cart-create-hints.helpers";
import {useCartText} from "../../cart.messages";
import {useRequiredLayers} from "../../../../utils/context";
import {CartCreateStore, CartStoreContext} from "../cart.create.store";
import {CartCreateController, CartCreateControllerContext} from "../cart.create.controller";
import "./cart-create-hints-table.scss";

export const CartCreateHintsTable: React.FC = observer(() => {
    const {t} = useTranslation("translation", {useSuspense: false});
    const text = useCartText();
    const {store, controller} = useRequiredLayers<CartCreateStore, CartCreateController>(
        CartStoreContext, CartCreateControllerContext
    );
    const [filter, setFilter] = useState<"all" | "full" | "partial">("all");
    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const [expandedKey, setExpandedKey] = useState<string | null>(null);
    const [pending, setPending] = useState<CartHintRow | null>(null);
    const [loadingKey, setLoadingKey] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const submitting = useRef(false);

    const useBackorder = store.useBackorder || store.type === "backorder";
    const rows = (store.cart?.hints || []).map(item => createHintRow(item, store.defaultWarehouses, useBackorder));
    const fullyCoveredCount = rows.filter(row => row.coverage.isFullyReplaceable).length;
    const busy = store.previewLoading || store.loading || loadingKey !== null;
    const pageSize = 6;
    const query = search.trim().toLocaleLowerCase();
    const filteredRows = rows.filter(row =>
        (filter === "all" || row.coverage.isFullyReplaceable === (filter === "full"))
        && [row.item.requestedNo, row.item.article.name, row.item.article.description]
            .some(value => value?.toLocaleLowerCase().includes(query))
    );
    const currentPage = Math.min(page, Math.max(1, Math.ceil(filteredRows.length / pageSize)));

    const submit = async (row: CartHintRow) => {
        if (submitting.current || row.coverage.qtyToAdd <= 0) return;
        submitting.current = true;
        setLoadingKey(row.key);
        setError(null);
        try {
            await controller.addToCart({article: row.item.article.id, qty: row.coverage.qtyToAdd});
            setPending(null);
        } catch {
            setError(text("ADD_FAILED"));
        } finally {
            submitting.current = false;
            setLoadingKey(null);
        }
    };

    const addToCart = (row: CartHintRow) => {
        if (busy || row.coverage.qtyToAdd <= 0) return;
        setError(null);
        if (useBackorder || row.coverage.isFullyReplaceable) {
            void submit(row);
        } else {
            setPending(row);
        }
    };

    const columns = columnsFactory({
        t, text, addToCart, expandedKey, loadingKey, disabled: busy,
        toggleDetails: row => setExpandedKey(expandedKey === row.key ? null : row.key),
    });

    if (!rows.length && !pending) return null;

    return (
        <>
            <Collapse className="cart-collapse-hints-panel" defaultActiveKey={["alternatives"]}>
                <Collapse.Panel
                    key="alternatives"
                    header={<span className="cart-hints-title">
                        {text("ALTERNATIVES")}<span className="cart-hints-count">{rows.length}</span>
                        <Tooltip title={text("ALTERNATIVES_HELP")}>
                            <span className="cart-hints-help" onClick={event => event.stopPropagation()}>
                                <FontAwesomeIcon icon={faCircleInfo} />
                            </span>
                        </Tooltip>
                    </span>}
                >
                    <div className="cart-hints-toolbar">
                        <div className="cart-hints-filters" role="group" aria-label={text("ALTERNATIVES")}>
                            {([
                                {key: "all", label: text("ALL"), count: rows.length},
                                {key: "full", label: text("FULL"), count: fullyCoveredCount, icon: faCheckCircle},
                                {key: "partial", label: text("PARTIAL"), count: rows.length - fullyCoveredCount, icon: faExclamationTriangle},
                            ] as const).map(option => (
                                <button
                                    key={option.key}
                                    type="button"
                                    className={`cart-hint-filter cart-hint-filter-${option.key}`}
                                    aria-pressed={filter === option.key}
                                    aria-label={`${option.label} ${option.count}`}
                                    onClick={() => {setFilter(option.key); setPage(1);}}
                                >
                                    {"icon" in option && <FontAwesomeIcon icon={option.icon} />}
                                    {option.label}<span>{option.count}</span>
                                </button>
                            ))}
                        </div>
                        <Input
                            className="cart-hints-search"
                            size="small"
                            prefix={<FontAwesomeIcon icon={faSearch} />}
                            allowClear
                            value={search}
                            aria-label={text("SEARCH")}
                            placeholder={text("SEARCH")}
                            onChange={event => {setSearch(event.target.value); setPage(1);}}
                        />
                    </div>
                    {error && !pending && <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} />}
                    <Table<CartHintRow>
                        className="basket-table cart-hints-table"
                        rowKey="key"
                        columns={columns}
                        tableLayout="fixed"
                        size="small"
                        dataSource={filteredRows}
                        loading={store.previewLoading || store.loading}
                        locale={{emptyText: text("NO_MATCHES")}}
                        rowClassName={row => row.coverage.isFullyReplaceable ? "hint-row-full" : "hint-row-partial"}
                        pagination={{
                            current: currentPage,
                            pageSize,
                            hideOnSinglePage: true,
                            showSizeChanger: false,
                            onChange: nextPage => {setPage(nextPage); setExpandedKey(null);},
                            showTotal: (total, range) => text("RANGE", {from: range[0], to: range[1], total}),
                            size: "small",
                        }}
                        expandable={{
                            showExpandColumn: false,
                            expandedRowKeys: expandedKey ? [expandedKey] : [],
                            expandedRowRender: row => <HintDetails row={row} warehouses={store.warehousesAll} text={text} />,
                        }}
                    />
                </Collapse.Panel>
            </Collapse>
            <Modal
                className="cart-hints-confirm-modal"
                visible={pending !== null}
                width={520}
                title={<span className="hint-confirm-title"><FontAwesomeIcon icon={faExclamationTriangle} />{text("PARTIAL_TITLE")}</span>}
                okText={text("CONFIRM_ADD", {qty: pending?.coverage.qtyToAdd || 0})}
                cancelText={t("NO")}
                confirmLoading={loadingKey !== null}
                cancelButtonProps={{disabled: loadingKey !== null}}
                closable={loadingKey === null}
                maskClosable={false}
                keyboard={loadingKey === null}
                onCancel={() => {if (!submitting.current) {setPending(null); setError(null);}}}
                onOk={() => pending && !busy && void submit(pending)}
            >
                {pending && <>
                    <p className="hint-confirm-explanation">{hintExplanation(pending, text)}</p>
                    <div className="hint-confirm-metrics">
                        <div><span>{text("REQUESTED")}</span><strong>{pending.item.requestedQty.toLocaleString()}</strong><small>{pending.item.requestedNo}</small></div>
                        <div><span>{text("REQUIRED")}</span><strong>{pending.coverage.requiredQty.toLocaleString()}</strong><small>{pending.item.article.name}</small></div>
                        <div className="is-partial"><span>{text("AVAILABLE")}</span><strong>{pending.coverage.availableQty.toLocaleString()}</strong><small>{pending.item.article.name}</small></div>
                    </div>
                    <p>{text("PARTIAL_QUESTION")}</p>
                    {error && <Alert type="error" showIcon message={error} />}
                </>}
            </Modal>
        </>
    );
});
